// API Service for PasteWaves

// Helper function to safely build URLs (removes trailing slashes to prevent double slashes)
const normalizeBaseUrl = (url: string): string => url.replace(/\/+$/, '');

const PRESIGNED_URL_LAMBDA = normalizeBaseUrl(process.env.REACT_APP_PRESIGNED_URL_LAMBDA || '');
const STATS_URL = normalizeBaseUrl(process.env.REACT_APP_STATS_URL || '');
const UPLOADS_URL = normalizeBaseUrl(process.env.REACT_APP_UPLOADS_URL || 'https://uploads.pastewaves.com');
const FILE_METADATA_URL = normalizeBaseUrl(process.env.REACT_APP_FILE_METADATA_URL || '');
const COMMENTS_API_URL = normalizeBaseUrl(process.env.REACT_APP_COMMENTS_API_URL || '');
const USER_STORAGE_URL = normalizeBaseUrl(process.env.REACT_APP_USER_STORAGE_URL || '');
const PROMO_CODE_URL = normalizeBaseUrl(process.env.REACT_APP_PROMO_CODE_URL || '');
const COLLECTIONS_URL = normalizeBaseUrl(process.env.REACT_APP_COLLECTIONS_URL || '');
const COLLECTION_LINKS_URL = normalizeBaseUrl(process.env.REACT_APP_COLLECTION_LINKS_URL || '');

// Debug log for collection links URL
console.log('COLLECTION_LINKS_URL:', COLLECTION_LINKS_URL);

export interface UploadResponse {
  success: boolean;
  fileId: string;
  shareUrl: string;
  message?: string;
  permanent?: boolean;
  storage_used?: number;
  storage_max?: number;
  over_quota?: boolean;
  claimToken?: string; // For anonymous uploads - allows claiming ownership later
}

export interface PresignedUrlResponse {
  upload_id: string;
  signed_url: string;
  s3_key: string;
  expires_at: string;
  cloudfront: boolean;
  max_file_size: number;
  claim_token?: string; // Anonymous uploads get claim token for later ownership transfer
}

export interface PersonalStorage {
  storage_bytes: number;
  file_count: number;
  storage_max: number;
  plan: string;
  // Audio/video/original breakdown — optional during backend rollout; missing values treated as 0.
  // storage_bytes will equal audio_storage_bytes + original_storage_bytes + video_storage_bytes once backend deploys.
  audio_storage_bytes?: number;
  video_storage_bytes?: number;
  video_count?: number;
  // Kept-Original (WAV / FLAC / AIF source) bytes + count — opt-in retention via Keep-Original toggle.
  // Backend (lambda-user-storage stats) doesn't emit these yet; gilfoyle's lane. Mirrors the video pair.
  original_storage_bytes?: number;
  original_count?: number;
}

export interface PromoCodeResponse {
  success: boolean;
  message: string;
  plan?: string;
}

export interface Collection {
  collection_id: string;
  collection_name: string;
  description?: string;
  visibility: 'public' | 'private';
  owner: string;
  owner_handle?: string; // present when the owner has claimed a public handle
  artwork_url?: string;
  artwork_thumbnail_url?: string;
  artwork_4x3_url?: string;
  artwork_width?: number;
  artwork_height?: number;
  artwork_updated_at?: number;
  file_count: number;
  created_at: number;
  updated_at: number;
  total_duration?: number;
  view_count?: number;
  migrated_from_list?: boolean;
}

export interface CollectionFile {
  file_id: string;
  title: string;
  filename: string;
  duration_seconds?: number;
  uploaded_at: number;
  source_type?: string;
  // File artwork (may not be present for all files)
  artwork_url?: string;
  artwork_thumbnail_url?: string;
  artwork_4x3_url?: string;
  artwork_thumb_url?: string; // 320x320 square thumbnail (small UI slots)
}

export interface DailyDataPoint {
  date: string;        // YYYY-MM-DD
  date_label: string;  // "Jan 2"
  value: number;       // Metric value for that day
}

export interface CloudFrontStatistics {
  // Summary metrics
  requests_24h: number;
  requests_7d: number;
  requests_30d: number;
  bytes_downloaded_24h: number;
  bytes_downloaded_7d: number;
  bytes_downloaded_30d: number;
  avg_cache_hit_rate: number;      // Percentage (0-100)
  avg_4xx_error_rate: number;      // Percentage (0-100)
  avg_5xx_error_rate: number;      // Percentage (0-100)

  // Daily time series (last 30 days)
  daily_requests: DailyDataPoint[];
  daily_bytes_downloaded: DailyDataPoint[];
  daily_cache_hit_rate: DailyDataPoint[];
  daily_4xx_errors: DailyDataPoint[];
  daily_5xx_errors: DailyDataPoint[];
}

export interface StorageInfo {
  filesCount: number;
  usedStorageGB: number;
  maxStorageGB: number;
  updatedAt: string;
  userCount?: number;       // Total number of users (for early supporter program)

  // Upload metrics
  totalCount: number;       // Total bytes uploaded since Oct 25
  totalVolume: number;      // Total number of uploads since Oct 25
  countToday: number;       // Bytes uploaded today
  volumeToday: number;      // Uploads today
  countYesterday: number;   // Bytes uploaded T-1 day
  volumeYesterday: number;  // Uploads T-1 day
  countLastWeek: number;    // Bytes uploaded T-1 week
  volumeLastWeek: number;   // Uploads T-1 week
  countLastMonth: number;   // Bytes uploaded T-1 month
  volumeLastMonth: number;  // Uploads T-1 month

  // CloudFront metrics
  cloudfront_statistics?: CloudFrontStatistics;

  // View metrics
  viewsLast7Days?: DailyDataPoint[];
}

export interface AudioFile {
  id: string;
  filename: string;
  url: string;
  uploadedAt: string;
  owner?: string; // User ID who owns the file (UUID)
  ownerName?: string; // Display name for the owner (email, name, etc.) - falls back to owner if not set
  ownerHandle?: string; // Owner's public-profile handle, present when they have a /@handle profile (backend-resolved)
  ownerDisplayName?: string; // Owner's public-profile display name (public_name); present when set
  title?: string;
  description?: string;
  tags?: string[]; // File tags for categorization
  list?: string; // Collection/playlist this file belongs to
  processingStatus?: string;
  originalSizeBytes?: number;
  compressedSizeBytes?: number;
  durationSeconds?: number;
  format?: string;
  bitrate?: string;
  sampleRate?: string;
  sourceType?: 'upload' | 'recording'; // Whether file was uploaded or recorded
  ttl?: number | null; // Expiration timestamp (Unix seconds), null/undefined = permanent file
  artworkUrl?: string; // 1:1 square artwork URL
  artworkThumbnailUrl?: string; // 16:9 blurred backdrop URL
  artwork4x3Url?: string; // 4:3 OpenGraph image URL
  artworkThumbUrl?: string; // 320x320 square thumbnail (small UI slots)
  artworkWidth?: number; // Original artwork width
  artworkHeight?: number; // Original artwork height
  artworkUpdatedAt?: number; // Unix timestamp when artwork was last updated
  viewCountTotal?: number; // All-time view count
  viewCount24h?: number; // Views in last 24 hours
  viewCount7d?: number; // Views in last 7 days
  viewCount30d?: number; // Views in last 30 days
  allowDownload?: boolean; // Privacy: Allow others to download this track (default: false)
  // Sparse share-video render state. Missing/undefined = never requested.
  // -1 = render pending, -2 = render failed (failureReason populated), positive int = bytes when ready.
  videoSize?: number;
  videoUrl?: string; // Populated when videoSize > 0
  videoFailureReason?: string; // Populated when videoSize === -2
  // Kept-Original (WAV / FLAC / AIF source) — present when the owner opted in
  // at upload time and the source bytes were retained in S3. Sparse: missing
  // means "no kept original on this file." Drives the WAV tile on the
  // Artifacts Shelf and the "Download original" / "Delete original" rows in
  // the More menu. Backend field names are snake_case in DDB
  // (`kept_original_size`, `kept_original_format`) but file-metadata Lambda
  // re-keys them to these camelCase names in its GET response.
  keptOriginalSizeBytes?: number;
  keptOriginalFormat?: string; // e.g. 'wav' | 'flac' | 'aif'
}

export interface PromoCodeResponse {
  success: boolean;
  message: string;
  plan?: string;
}

export interface Comment {
  id: string;
  fileId: string;
  parentId?: string; // null for top-level, set for replies
  author: string;
  authorName: string;
  text: string;
  timestamp: number; // Audio timestamp in seconds
  createdAt: number; // Unix timestamp
  updatedAt?: number; // Unix timestamp, set when edited
  replyCount?: number; // Number of replies (for top-level comments)
  replies?: Comment[]; // Nested replies (optional, populated by frontend)
}

export interface CreateCommentRequest {
  fileId: string;
  parentId?: string; // Set to create a reply to a specific comment
  text: string;
  audioTimestamp?: number;
  author?: string;
}

/**
 * Get presigned URL from Lambda
 * SECURITY: Backend determines permanent storage based on JWT plan claim
 */
const getPresignedUrl = async (
  filename: string,
  contentType: string,
  fileSize: number,
  title?: string,
  description?: string,
  sourceType?: 'upload' | 'recording',
  idToken?: string,
  // Pro/Beta opt-in: ask the backend to retain the user's original source
  // file (WAV / FLAC / AIF / etc.) alongside the MP3 we play. Sent as
  // `keep_original: true` in the JSON body to match the endpoint's existing
  // snake_case convention (`file_name`, `content_type`, `source_type`, …).
  // The audio Lambda will honor this by skipping the post-transcode delete
  // and writing a sentinel on the file record. Until the backend lands the
  // field is an unknown JSON key and ignored — additive request contract.
  keepOriginal?: boolean,
): Promise<PresignedUrlResponse> => {
  if (!PRESIGNED_URL_LAMBDA) {
    console.error('PRESIGNED_URL_LAMBDA is not configured!');
    throw new Error('Upload service is not configured. Please check your environment variables.');
  }

  console.log('Requesting presigned URL from:', PRESIGNED_URL_LAMBDA);

  const headers: Record<string, string> = {
    'Content-Type': 'application/json',
  };

  // Add Authorization header if user is authenticated
  if (idToken) {
    headers['Authorization'] = `Bearer ${idToken}`;
  }

  const response = await fetch(PRESIGNED_URL_LAMBDA, {
    method: 'POST',
    headers,
    body: JSON.stringify({
      file_name: filename,
      content_type: contentType,
      file_size: fileSize,
      title: title,
      description: description,
      source_type: sourceType || 'upload', // Default to 'upload' if not specified
      // Only emit the key when explicitly true so the request body stays
      // byte-identical to today's shape for the (overwhelming) free-tier path.
      // Unknown fields are tolerated by API Gateway + the Go Lambda's JSON
      // unmarshaller until gilfoyle wires the backend half.
      ...(keepOriginal === true ? { keep_original: true } : {}),
      // Backend determines permanent storage based on JWT plan claim (not client input)
    }),
  });

  if (!response.ok) {
    const errorText = await response.text();
    console.error('Presigned URL request failed:', response.status, errorText);
    throw new Error(`Failed to get presigned URL: ${response.statusText}`);
  }

  return await response.json();
};

/**
 * Upload an audio file to S3 using presigned URL
 * Enhanced with iOS-specific fixes for better mobile compatibility
 * SECURITY: Backend determines permanent storage based on JWT plan claim
 */
export const uploadAudioFile = async (
  file: File,
  onProgress?: (progress: number) => void,
  title?: string,
  description?: string,
  sourceType?: 'upload' | 'recording',
  idToken?: string,
  // Pro/Beta opt-in: thread through to the presigned-URL request body.
  // Forwarded as `keep_original: true` when true; omitted when false/undef
  // so the wire shape is unchanged for the default path.
  keepOriginal?: boolean,
): Promise<UploadResponse> => {
  try {
    // iOS Fix 1: Normalize content type - iOS sometimes uses non-standard MIME types.
    // Video sources pass through unchanged so the S3 PUT Content-Type and stored
    // metadata reflect the actual container; the audio-processor Lambda inspects
    // the prefix and extracts the audio stream via ffmpeg.
    let contentType = file.type || 'audio/mpeg';
    if (!contentType.startsWith('audio/') && !contentType.startsWith('video/')) {
      contentType = 'audio/mpeg'; // Fallback for iOS / unknown / empty
    }

    console.log('Upload starting:', {
      fileName: file.name,
      fileSize: file.size,
      contentType: contentType,
      originalType: file.type
    });

    // Step 1: Get presigned URL from Lambda
    if (onProgress) onProgress(10);
    const presignedData = await getPresignedUrl(file.name, contentType, file.size, title, description, sourceType, idToken, keepOriginal);
    console.log('Presigned URL obtained:', { uploadId: presignedData.upload_id });

    // Step 2: Upload file directly to S3 using presigned URL with progress tracking
    if (onProgress) onProgress(20);

    // iOS Fix 2: Try fetch API first (better iOS support), fallback to XHR
    const isIOS = /iPad|iPhone|iPod/.test(navigator.userAgent) ||
                  (navigator.platform === 'MacIntel' && navigator.maxTouchPoints > 1);

    console.log('Detected iOS device:', isIOS);

    if (isIOS) {
      // Use fetch API for iOS - better support and fewer CORS issues
      console.log('Using fetch API for iOS upload');

      const response = await fetch(presignedData.signed_url, {
        method: 'PUT',
        body: file,
        headers: {
          'Content-Type': contentType,
        },
      });

      if (!response.ok) {
        const errorText = await response.text();
        console.error('S3 upload failed:', response.status, errorText);
        throw new Error(`Upload failed: ${response.statusText}`);
      }

      console.log('iOS upload completed successfully');
      if (onProgress) onProgress(95);
    } else {
      // Use XHR with progress tracking for non-iOS devices
      console.log('Using XHR for upload with progress tracking');

      await new Promise<void>((resolve, reject) => {
        const xhr = new XMLHttpRequest();

        xhr.upload.addEventListener('progress', (event) => {
          if (event.lengthComputable && onProgress) {
            // Calculate progress from 20% to 95% during upload
            const uploadProgress = 20 + (event.loaded / event.total) * 75;
            onProgress(Math.round(uploadProgress));
          }
        });

        xhr.addEventListener('load', () => {
          if (xhr.status >= 200 && xhr.status < 300) {
            console.log('XHR upload completed successfully');
            if (onProgress) onProgress(95);
            resolve();
          } else {
            console.error('S3 upload failed:', xhr.status, xhr.statusText);
            reject(new Error(`S3 upload failed: ${xhr.statusText}`));
          }
        });

        xhr.addEventListener('error', (e) => {
          console.error('XHR error:', e);
          reject(new Error('Upload failed'));
        });

        xhr.addEventListener('abort', () => {
          console.warn('XHR aborted');
          reject(new Error('Upload cancelled'));
        });

        xhr.open('PUT', presignedData.signed_url);
        xhr.setRequestHeader('Content-Type', contentType);

        // Note: Title/description metadata is sent to Lambda for future use,
        // but not included in the S3 upload headers to avoid presigned URL complications.
        // The audio processor can add metadata after processing if needed.

        console.log('Sending file via XHR...');
        xhr.send(file);
      });
    }

    // Step 3: Return success response with share URL
    // Use simple file ID - audio will be processed to uploads/{id}/audio.mp3
    if (onProgress) onProgress(100);
    console.log('Upload process complete');

    return {
      success: true,
      fileId: presignedData.upload_id,
      shareUrl: `${window.location.origin}/player/${presignedData.upload_id}`,
      message: 'File uploaded successfully. Processing audio...',
      claimToken: presignedData.claim_token, // For anonymous uploads - allows claiming ownership later
    };
  } catch (error) {
    console.error('Error uploading file:', error);
    throw error;
  }
};

/**
 * Upload artwork for an existing audio file
 * Requires authentication - only file owner can upload artwork
 */
export const uploadArtwork = async (
  fileId: string,
  imageFile: File,
  idToken: string,
  onProgress?: (progress: number) => void
): Promise<{ success: boolean; message: string }> => {
  try {
    if (!PRESIGNED_URL_LAMBDA) {
      throw new Error('Upload service is not configured');
    }

    // Validate image file
    if (!imageFile.type.startsWith('image/')) {
      throw new Error('Only image files are allowed for artwork');
    }

    // Max artwork size: 10MB
    const maxSize = 10 * 1024 * 1024;
    if (imageFile.size > maxSize) {
      throw new Error('Artwork file too large. Maximum size is 10MB');
    }

    console.log('Artwork upload starting:', {
      fileId,
      fileName: imageFile.name,
      fileSize: imageFile.size,
      contentType: imageFile.type
    });

    // Step 1: Get presigned URL for artwork upload
    if (onProgress) onProgress(10);

    // Call artwork upload endpoint (path contains "artwork")
    const artworkUploadUrl = `${PRESIGNED_URL_LAMBDA}/artwork`;

    const response = await fetch(artworkUploadUrl, {
      method: 'POST',
      headers: {
        'Content-Type': 'application/json',
        'Authorization': `Bearer ${idToken}`
      },
      body: JSON.stringify({
        file_id: fileId,
        file_name: imageFile.name,
        content_type: imageFile.type,
        file_size: imageFile.size
      })
    });

    if (!response.ok) {
      const errorData = await response.json().catch(() => ({ error: 'Failed to upload artwork' }));
      throw new Error(errorData.error || 'Failed to get artwork upload URL');
    }

    const presignedData = await response.json();
    console.log('Artwork presigned URL obtained:', presignedData);

    // Step 2: Upload image to S3 via XHR so onProgress reports real bytes,
    // not a fake 10/30/100 step function. fetch() doesn't expose upload
    // progress events; XHR's upload.onprogress does.
    await new Promise<void>((resolve, reject) => {
      const xhr = new XMLHttpRequest();
      xhr.open('PUT', presignedData.signed_url);
      xhr.setRequestHeader('Content-Type', imageFile.type);
      if (onProgress) {
        xhr.upload.onprogress = (e) => {
          if (e.lengthComputable) {
            onProgress(Math.round((e.loaded / e.total) * 100));
          }
        };
      }
      xhr.onload = () => {
        if (xhr.status >= 200 && xhr.status < 300) {
          resolve();
        } else {
          console.error('S3 artwork upload failed:', xhr.status, xhr.responseText);
          reject(new Error('Failed to upload artwork to storage'));
        }
      };
      xhr.onerror = () => reject(new Error('Network error during artwork upload'));
      xhr.onabort = () => reject(new Error('Artwork upload aborted'));
      xhr.send(imageFile);
    });

    console.log('Artwork uploaded successfully');
    if (onProgress) onProgress(100);

    return {
      success: true,
      message: 'Artwork uploaded successfully. Processing...'
    };
  } catch (error) {
    console.error('Error uploading artwork:', error);
    throw error;
  }
};

/**
 * Get storage usage information from dedicated stats Lambda endpoint
 */
export const getStorageInfo = async (): Promise<StorageInfo> => {
  if (!STATS_URL) {
    console.warn('STATS_URL not configured, returning default storage info');
    return {
      filesCount: 0,
      usedStorageGB: 0,
      maxStorageGB: 1024, // 1TB for free tier
      updatedAt: new Date().toISOString(),
      totalCount: 0,
      totalVolume: 0,
      countToday: 0,
      volumeToday: 0,
      countYesterday: 0,
      volumeYesterday: 0,
      countLastWeek: 0,
      volumeLastWeek: 0,
      countLastMonth: 0,
      volumeLastMonth: 0,
    };
  }

  try {
    console.log('Fetching storage stats from:', STATS_URL);

    const response = await fetch(STATS_URL, {
      method: 'GET',
      headers: {
        'Content-Type': 'application/json',
      },
    });

    if (!response.ok) {
      throw new Error(`Failed to fetch storage info: ${response.statusText}`);
    }

    const data = await response.json();
    return data;
  } catch (error) {
    console.error('Error fetching storage info:', error);
    // Return default data if API is not available
    return {
      filesCount: 0,
      usedStorageGB: 0,
      maxStorageGB: 1024, // 1TB for free tier
      updatedAt: new Date().toISOString(),
      totalCount: 0,
      totalVolume: 0,
      countToday: 0,
      volumeToday: 0,
      countYesterday: 0,
      volumeYesterday: 0,
      countLastWeek: 0,
      volumeLastWeek: 0,
      countLastMonth: 0,
      volumeLastMonth: 0,
    };
  }
};

/**
 * Get audio file information by file ID
 *
 * Fetches file metadata from DynamoDB via the file-metadata Lambda,
 * including title, description, file sizes, and processing status.
 */
export const getAudioFile = async (fileId: string): Promise<AudioFile> => {
  try {
    if (!FILE_METADATA_URL) {
      console.warn('FILE_METADATA_URL not configured, using fallback');
      // Fallback to basic file info
      return {
        id: fileId,
        filename: `audio-${fileId}.mp3`,
        url: `${UPLOADS_URL}/uploads/${fileId}/audio.mp3`,
        uploadedAt: new Date().toISOString(),
      };
    }

    // Fetch metadata from Lambda
    const metadataUrl = `${FILE_METADATA_URL}/${fileId}`;
    console.log('Fetching file metadata from:', metadataUrl);

    const response = await fetch(metadataUrl, {
      method: 'GET',
      headers: {
        'Content-Type': 'application/json',
      },
    });

    if (!response.ok) {
      if (response.status === 404) {
        throw new Error('FILE_NOT_FOUND');
      }
      throw new Error(`Failed to fetch file metadata: ${response.statusText}`);
    }

    const metadata = await response.json();
    console.log('File metadata retrieved:', metadata);

    return metadata;
  } catch (error) {
    console.error('Error fetching audio file:', error);

    // Re-throw 404 errors so they can be handled properly
    if (error instanceof Error && error.message === 'FILE_NOT_FOUND') {
      throw error;
    }

    // Fallback to basic file info with pending status for other errors
    // This ensures the player shows processing screen instead of error for network issues
    return {
      id: fileId,
      filename: `audio-${fileId}.mp3`,
      url: `${UPLOADS_URL}/uploads/${fileId}/audio.mp3`,
      uploadedAt: new Date().toISOString(),
      processingStatus: 'pending',
    };
  }
};

/**
 * Get comments for a specific audio file
 * Returns nested structure with top-level comments and their replies
 */
export const getComments = async (fileId: string): Promise<Comment[]> => {
  try {
    if (!COMMENTS_API_URL) {
      console.warn('COMMENTS_API_URL not configured, returning empty comments');
      return [];
    }

    const response = await fetch(`${COMMENTS_API_URL}/comments/${fileId}`, {
      method: 'GET',
      headers: {
        'Content-Type': 'application/json',
      },
    });

    if (!response.ok) {
      throw new Error(`Failed to fetch comments: ${response.statusText}`);
    }

    const data = await response.json();

    // Transform nested response structure
    const topLevelComments: Comment[] = data.comments || [];
    const repliesMap: Record<string, Comment[]> = data.replies || {};

    // Attach replies to their parent comments
    topLevelComments.forEach(comment => {
      if (repliesMap[comment.id]) {
        comment.replies = repliesMap[comment.id];
      }
    });

    return topLevelComments;
  } catch (error) {
    console.error('Error fetching comments:', error);
    // Return empty array if API is not available
    return [];
  }
};

/**
 * Create a new comment on an audio file
 * Requires authentication - must pass idToken
 */
export const createComment = async (commentData: CreateCommentRequest, idToken?: string): Promise<Comment> => {
  try {
    if (!COMMENTS_API_URL) {
      throw new Error('Comments API not configured');
    }

    const headers: Record<string, string> = {
      'Content-Type': 'application/json',
    };

    // ADD AUTH HEADER IF TOKEN PROVIDED
    if (idToken) {
      headers['Authorization'] = `Bearer ${idToken}`;
    }

    const requestBody: any = {
      fileId: commentData.fileId,
      timestamp: commentData.audioTimestamp || 0,
      text: commentData.text,
      authorName: commentData.author || 'User',
    };

    // Add parentId if this is a reply
    if (commentData.parentId) {
      requestBody.parentId = commentData.parentId;
    }

    const response = await fetch(`${COMMENTS_API_URL}/comments`, {
      method: 'POST',
      headers,
      body: JSON.stringify(requestBody),
    });

    if (!response.ok) {
      if (response.status === 401) {
        throw new Error('Authentication required to post comments');
      }
      if (response.status === 400) {
        const errorData = await response.json();
        throw new Error(errorData.error || 'Failed to create comment');
      }
      throw new Error(`Failed to create comment: ${response.statusText}`);
    }

    const data = await response.json();
    return data.comment;
  } catch (error) {
    console.error('Error creating comment:', error);
    throw error;
  }
};

/**
 * Edit an existing comment
 * Requires authentication and must be within 5 minutes of creation
 */
export const editComment = async (commentId: string, text: string, idToken: string): Promise<Comment> => {
  try {
    if (!COMMENTS_API_URL) {
      throw new Error('Comments API not configured');
    }

    const response = await fetch(`${COMMENTS_API_URL}/comments/${commentId}`, {
      method: 'PATCH',
      headers: {
        'Content-Type': 'application/json',
        'Authorization': `Bearer ${idToken}`,
      },
      body: JSON.stringify({ text }),
    });

    if (!response.ok) {
      if (response.status === 401) {
        throw new Error('Authentication required to edit comments');
      }
      if (response.status === 403) {
        const errorData = await response.json();
        throw new Error(errorData.error || 'You can only edit your own comments within 5 minutes');
      }
      throw new Error(`Failed to edit comment: ${response.statusText}`);
    }

    const data = await response.json();
    return data.comment;
  } catch (error) {
    console.error('Error editing comment:', error);
    throw error;
  }
};

/**
 * Delete a comment
 */
export const deleteComment = async (commentId: string, idToken?: string): Promise<void> => {
  try {
    if (!COMMENTS_API_URL) {
      throw new Error('Comments API not configured');
    }

    const headers: Record<string, string> = {
      'Content-Type': 'application/json',
    };

    if (idToken) {
      headers['Authorization'] = `Bearer ${idToken}`;
    }

    const response = await fetch(`${COMMENTS_API_URL}/comments/${commentId}`, {
      method: 'DELETE',
      headers,
    });

    if (!response.ok) {
      throw new Error(`Failed to delete comment: ${response.statusText}`);
    }
  } catch (error) {
    console.error('Error deleting comment:', error);
    throw error;
  }
};

/**
 * Get files uploaded by a specific user
 *
 * Fetches all files for a user from the file-metadata Lambda's user endpoint.
 * Requires authentication - must pass ID token in Authorization header.
 */
export const getUserFiles = async (userId: string, idToken: string): Promise<AudioFile[]> => {
  try {
    if (!FILE_METADATA_URL) {
      console.warn('FILE_METADATA_URL not configured');
      return [];
    }

    const userFilesUrl = `${FILE_METADATA_URL}/user/${userId}/files`;
    console.log('Fetching user files from:', userFilesUrl);

    const response = await fetch(userFilesUrl, {
      method: 'GET',
      headers: {
        'Content-Type': 'application/json',
        'Authorization': `Bearer ${idToken}`
      },
    });

    if (!response.ok) {
      if (response.status === 404) {
        console.log('No files found for user');
        return [];
      }
      throw new Error(`Failed to fetch user files: ${response.statusText}`);
    }

    const data = await response.json();
    console.log('User files retrieved:', data);

    // Handle both array response and object with files array
    return Array.isArray(data) ? data : (data.files || []);
  } catch (error) {
    console.error('Error fetching user files:', error);
    throw error;
  }
};

/**
 * Delete an audio file
 *
 * Deletes a file from both S3 and DynamoDB. Requires authentication.
 * The user must own the file (verified by the Lambda).
 */
export const deleteAudioFile = async (fileId: string, idToken: string): Promise<void> => {
  try {
    if (!FILE_METADATA_URL) {
      throw new Error('FILE_METADATA_URL not configured');
    }

    const deleteUrl = `${FILE_METADATA_URL}/${fileId}`;
    console.log('Deleting file:', deleteUrl);

    const response = await fetch(deleteUrl, {
      method: 'DELETE',
      headers: {
        'Authorization': `Bearer ${idToken}`,
        'Content-Type': 'application/json',
      },
    });

    if (!response.ok) {
      const errorText = await response.text();
      console.error('Delete failed:', response.status, errorText);

      if (response.status === 401) {
        throw new Error('Authentication required to delete files');
      } else if (response.status === 403) {
        throw new Error('You do not have permission to delete this file');
      } else if (response.status === 404) {
        throw new Error('File not found');
      } else {
        throw new Error(`Failed to delete file: ${response.status}`);
      }
    }

    console.log('File deleted successfully');
  } catch (error) {
    console.error('Error deleting file:', error);
    throw error;
  }
};

/**
 * Get user's personal storage statistics
 * Requires authentication - must pass ID token
 */
export const getUserStorage = async (idToken: string): Promise<PersonalStorage> => {
  try {
    if (!USER_STORAGE_URL) {
      throw new Error('USER_STORAGE_URL not configured');
    }

    // Append /user/storage path to the base URL
    const url = `${USER_STORAGE_URL.replace(/\/$/, '')}/user/storage`;
    console.log('[getUserStorage] Fetching from:', url);

    const response = await fetch(url, {
      method: 'GET',
      headers: {
        'Content-Type': 'application/json',
        'Authorization': `Bearer ${idToken}`,
      },
    });

    if (!response.ok) {
      if (response.status === 401) {
        throw new Error('Authentication required to view storage stats');
      }
      throw new Error(`Failed to fetch user storage: ${response.statusText}`);
    }

    const data = await response.json();
    return data;
  } catch (error) {
    console.error('Error fetching user storage:', error);
    throw error;
  }
};

/**
 * Make a temporary file permanent (remove TTL)
 *
 * Removes the TTL attribute from a file, making it permanent.
 * This action cannot be undone. Requires authentication and file ownership.
 * May fail if user exceeds storage quota.
 */
export const makeFilePermanent = async (fileId: string, idToken: string): Promise<AudioFile> => {
  try {
    if (!FILE_METADATA_URL) {
      throw new Error('FILE_METADATA_URL not configured');
    }

    const url = `${FILE_METADATA_URL}/${fileId}/permanent`;
    console.log('Making file permanent:', url);

    const response = await fetch(url, {
      method: 'PATCH',
      headers: {
        'Authorization': `Bearer ${idToken}`,
        'Content-Type': 'application/json',
      },
    });

    if (!response.ok) {
      const errorText = await response.text();
      console.error('Make permanent failed:', response.status, errorText);

      if (response.status === 401) {
        throw new Error('Authentication required to make files permanent');
      } else if (response.status === 403) {
        // Parse error JSON to get specific error message
        try {
          const errorData = JSON.parse(errorText);
          throw new Error(errorData.error || 'You do not have permission to modify this file');
        } catch {
          throw new Error('You do not have permission to modify this file');
        }
      } else if (response.status === 404) {
        throw new Error('File not found');
      } else if (response.status === 400) {
        // Quota exceeded or other validation error
        try {
          const errorData = JSON.parse(errorText);
          throw new Error(errorData.error || 'Failed to make file permanent');
        } catch {
          throw new Error('Failed to make file permanent');
        }
      } else {
        throw new Error(`Failed to make file permanent: ${response.status}`);
      }
    }

    const data = await response.json();
    console.log('File made permanent successfully:', data);
    return data;
  } catch (error) {
    console.error('Error making file permanent:', error);
    throw error;
  }
};

/**
 * Request a share-video render (mp4 with artwork backdrop + audio).
 *
 * POSTs to /files/{id}/video. Server enqueues an ffmpeg job, flips
 * video_size to -1, and returns the updated AudioFile. Open to every
 * signed-in owner — free callers get a watermarked output, Pro/Beta get
 * a clean one. The only remaining server precondition is that the file
 * has artwork (412); the UI mirrors that gate up front so the call
 * rarely reaches that branch in practice.
 */
export const requestVideoRender = async (fileId: string, idToken: string): Promise<AudioFile> => {
  try {
    if (!FILE_METADATA_URL) {
      throw new Error('FILE_METADATA_URL not configured');
    }

    const url = `${FILE_METADATA_URL}/${fileId}/video`;
    console.log('Requesting share-video render:', url);

    const response = await fetch(url, {
      method: 'POST',
      headers: {
        'Authorization': `Bearer ${idToken}`,
        'Content-Type': 'application/json',
      },
    });

    if (!response.ok) {
      const errorText = await response.text();
      console.error('Request video render failed:', response.status, errorText);

      if (response.status === 401) {
        throw new Error('Sign in to export video');
      } else if (response.status === 403) {
        try {
          const errorData = JSON.parse(errorText);
          throw new Error(errorData.error || 'You do not have permission to export this file');
        } catch {
          throw new Error('You do not have permission to export this file');
        }
      } else if (response.status === 404) {
        throw new Error('File not found');
      } else if (response.status === 409) {
        // Already pending or already rendered — second click on a row mid-render.
        throw new Error('An export is already in progress for this file');
      } else if (response.status === 412) {
        // Server-enforced precondition — file has no artwork.
        try {
          const errorData = JSON.parse(errorText);
          throw new Error(errorData.error || 'Add artwork to your file first');
        } catch {
          throw new Error('Add artwork to your file first');
        }
      } else {
        throw new Error(`Failed to export video: ${response.status}`);
      }
    }

    const data = await response.json();
    console.log('Share-video render queued:', data);
    return data;
  } catch (error) {
    console.error('Error requesting share-video render:', error);
    throw error;
  }
};

/**
 * Re-render an existing share-video. Used after the user changes artwork,
 * trims the audio, or otherwise wants a fresh export from the current state
 * of the file.
 *
 * POSTs to /files/{id}/video/remake. The server-side contract:
 *   - 200 → render kicked, response body is the updated AudioFile with
 *           videoSize=-1 and videoUrl absent (omitempty).
 *   - 409 → file is not in a state that supports remake. Body is
 *           {state: 'idle'|'pending'|'failed'|'ready'}; we throw
 *           VideoRenderConflictError so callers can re-route (e.g. "remake
 *           on idle" → silently call requestVideoRender instead).
 *   - 412 → no artwork. Same error UX as requestVideoRender.
 *
 * Open to every signed-in owner; tier-based watermarking is server-decided
 * exactly as in requestVideoRender.
 */
export const remakeVideoRender = async (fileId: string, idToken: string): Promise<AudioFile> => {
  if (!FILE_METADATA_URL) {
    throw new Error('FILE_METADATA_URL not configured');
  }
  const url = `${FILE_METADATA_URL}/${fileId}/video/remake`;
  console.log('Requesting share-video remake:', url);

  const response = await fetch(url, {
    method: 'POST',
    headers: {
      'Authorization': `Bearer ${idToken}`,
      'Content-Type': 'application/json',
    },
  });

  if (!response.ok) {
    const errorText = await response.text();
    console.error('Remake video render failed:', response.status, errorText);

    if (response.status === 401) {
      throw new Error('Sign in to remake video');
    } else if (response.status === 403) {
      try {
        const errorData = JSON.parse(errorText);
        throw new Error(errorData.error || 'You do not have permission to remake this file');
      } catch {
        throw new Error('You do not have permission to remake this file');
      }
    } else if (response.status === 404) {
      throw new Error('File not found');
    } else if (response.status === 409) {
      // State-classifier conflict — body tells us what the server sees.
      try {
        const errorData = JSON.parse(errorText);
        const state = errorData?.state as VideoRenderState | undefined;
        if (state === 'idle' || state === 'pending' || state === 'failed' || state === 'ready') {
          throw new VideoRenderConflictError(state);
        }
      } catch (e) {
        if (e instanceof VideoRenderConflictError) throw e;
      }
      throw new Error('Cannot remake video in its current state');
    } else if (response.status === 412) {
      try {
        const errorData = JSON.parse(errorText);
        throw new Error(errorData.error || 'Add artwork to your file first');
      } catch (e) {
        if (e instanceof Error && e.message) throw e;
        throw new Error('Add artwork to your file first');
      }
    } else {
      throw new Error(`Could not start remake: ${response.status}`);
    }
  }

  const data = await response.json();
  console.log('Share-video remake queued:', data);
  return data;
};

/**
 * Delete an existing share-video. Removes the rendered MP4 from S3 and
 * clears the video fields on the file metadata so the share-video state
 * machine goes back to idle.
 *
 * DELETEs /files/{id}/video. The server-side contract:
 *   - 200 → deleted. Response body is the updated AudioFile with
 *           videoSize/videoUrl/videoFailureReason absent (omitempty —
 *           the client treats absent keys as "the field was cleared").
 *   - 409 → file isn't in a state that supports delete (e.g. nothing to
 *           delete because state is 'idle', or render is mid-flight and
 *           the server doesn't want to race the renderer). Body is
 *           {state}; we throw VideoRenderConflictError.
 */
export const deleteVideoRender = async (fileId: string, idToken: string): Promise<AudioFile> => {
  if (!FILE_METADATA_URL) {
    throw new Error('FILE_METADATA_URL not configured');
  }
  const url = `${FILE_METADATA_URL}/${fileId}/video`;
  console.log('Requesting share-video delete:', url);

  const response = await fetch(url, {
    method: 'DELETE',
    headers: {
      'Authorization': `Bearer ${idToken}`,
      'Content-Type': 'application/json',
    },
  });

  if (!response.ok) {
    const errorText = await response.text();
    console.error('Delete video render failed:', response.status, errorText);

    if (response.status === 401) {
      throw new Error('Sign in to delete video');
    } else if (response.status === 403) {
      try {
        const errorData = JSON.parse(errorText);
        throw new Error(errorData.error || 'You do not have permission to delete this video');
      } catch {
        throw new Error('You do not have permission to delete this video');
      }
    } else if (response.status === 404) {
      throw new Error('File not found');
    } else if (response.status === 409) {
      try {
        const errorData = JSON.parse(errorText);
        const state = errorData?.state as VideoRenderState | undefined;
        if (state === 'idle' || state === 'pending' || state === 'failed' || state === 'ready') {
          throw new VideoRenderConflictError(state);
        }
      } catch (e) {
        if (e instanceof VideoRenderConflictError) throw e;
      }
      throw new Error('Cannot delete video in its current state');
    } else {
      throw new Error(`Failed to delete video: ${response.status}`);
    }
  }

  const data = await response.json();
  console.log('Share-video delete acknowledged:', data);
  return data;
};

/**
 * Server response shape for a presigned kept-original download URL. The URL is
 * short-lived (expires_at is when the presign falls over). Filename and
 * format come from the audio Lambda's record of the original upload so the
 * browser's Save dialog shows the user's original filename, not the file id.
 */
export interface KeptOriginalDownloadResponse {
  url: string;
  filename: string;
  size_bytes: number;
  format: string;
  expires_at: string;
}

/**
 * Get a presigned URL to download the retained source file (WAV / FLAC / AIF).
 * Owner-only — backend enforces via JWT against the file's owner field.
 *
 * GETs /files/{id}/original. Contract:
 *   - 200 → {url, filename, size_bytes, format, expires_at}
 *   - 401 → missing/invalid auth
 *   - 403 → authenticated but not the owner
 *   - 404 → no kept original. Body `{state: 'absent'|'pending'|'failed'}`
 *           lets the caller distinguish "never retained" from "still being
 *           retained" from "retention failed." We throw
 *           KeptOriginalConflictError so the caller can reconcile its UI.
 */
export const getKeptOriginalDownloadUrl = async (
  fileId: string,
  idToken: string,
): Promise<KeptOriginalDownloadResponse> => {
  if (!FILE_METADATA_URL) {
    throw new Error('FILE_METADATA_URL not configured');
  }
  const url = `${FILE_METADATA_URL}/${fileId}/original`;
  console.log('Requesting kept-original download URL:', url);

  const response = await fetch(url, {
    method: 'GET',
    headers: {
      'Authorization': `Bearer ${idToken}`,
      'Content-Type': 'application/json',
    },
  });

  if (!response.ok) {
    const errorText = await response.text();
    console.error('Get kept-original failed:', response.status, errorText);

    if (response.status === 401) {
      throw new Error('Sign in to download the original file');
    } else if (response.status === 403) {
      throw new Error('You do not have permission to download this original');
    } else if (response.status === 404) {
      try {
        const errorData = JSON.parse(errorText);
        const state = errorData?.state as KeptOriginalState | undefined;
        if (state === 'absent' || state === 'pending' || state === 'failed') {
          throw new KeptOriginalConflictError(state);
        }
      } catch (e) {
        if (e instanceof KeptOriginalConflictError) throw e;
      }
      throw new Error('Original file not available');
    } else {
      throw new Error(`Failed to get original: ${response.status}`);
    }
  }

  const data = await response.json();
  return data;
};

/**
 * Delete the retained source file. Owner-only. Frees the storage budget on
 * the user's Pro/Beta plan immediately.
 *
 * DELETEs /files/{id}/original. Contract:
 *   - 204 → deleted (no body)
 *   - 401 / 403 / 404 → as getKeptOriginalDownloadUrl above
 *   - 409 → state is `pending` (audio processor is still flipping the
 *           sentinel; CAS guard prevents racing the retention path). Caller
 *           shows a brief "still processing — try again" toast.
 */
export const deleteKeptOriginal = async (
  fileId: string,
  idToken: string,
): Promise<void> => {
  if (!FILE_METADATA_URL) {
    throw new Error('FILE_METADATA_URL not configured');
  }
  const url = `${FILE_METADATA_URL}/${fileId}/original`;
  console.log('Requesting kept-original delete:', url);

  const response = await fetch(url, {
    method: 'DELETE',
    headers: {
      'Authorization': `Bearer ${idToken}`,
      'Content-Type': 'application/json',
    },
  });

  if (response.ok) {
    return; // 204 No Content
  }

  const errorText = await response.text();
  console.error('Delete kept-original failed:', response.status, errorText);

  if (response.status === 401) {
    throw new Error('Sign in to delete the original file');
  } else if (response.status === 403) {
    throw new Error('You do not have permission to delete this original');
  } else if (response.status === 404) {
    try {
      const errorData = JSON.parse(errorText);
      const state = errorData?.state as KeptOriginalState | undefined;
      if (state === 'absent' || state === 'pending' || state === 'failed') {
        throw new KeptOriginalConflictError(state);
      }
    } catch (e) {
      if (e instanceof KeptOriginalConflictError) throw e;
    }
    throw new Error('Original file not available');
  } else if (response.status === 409) {
    throw new KeptOriginalConflictError('pending');
  } else {
    throw new Error(`Failed to delete original: ${response.status}`);
  }
};

/**
 * Claim ownership of an anonymous file
 *
 * Transfers ownership of an anonymously uploaded file to the authenticated user.
 * Requires both the claim token (from upload) and valid JWT authentication.
 *
 * @param fileId - The file ID to claim
 * @param claimToken - The claim token received during anonymous upload
 * @param idToken - JWT authentication token
 * @returns Updated file metadata with new owner information
 */
export const claimFile = async (fileId: string, claimToken: string, idToken: string): Promise<AudioFile> => {
  try {
    if (!FILE_METADATA_URL) {
      throw new Error('FILE_METADATA_URL not configured');
    }

    const url = `${FILE_METADATA_URL}/${fileId}/claim`;
    console.log('Claiming file:', url);

    const response = await fetch(url, {
      method: 'POST',
      headers: {
        'Authorization': `Bearer ${idToken}`,
        'Content-Type': 'application/json',
      },
      body: JSON.stringify({
        claim_token: claimToken,
      }),
    });

    if (!response.ok) {
      const errorText = await response.text();
      console.error('Claim file failed:', response.status, errorText);

      if (response.status === 401) {
        throw new Error('Authentication required to claim files');
      } else if (response.status === 403) {
        // Invalid or expired claim token
        try {
          const errorData = JSON.parse(errorText);
          throw new Error(errorData.error || 'Invalid or expired claim token');
        } catch {
          throw new Error('Invalid or expired claim token');
        }
      } else if (response.status === 404) {
        throw new Error('File not found');
      } else if (response.status === 400) {
        // File already claimed or other validation error
        try {
          const errorData = JSON.parse(errorText);
          throw new Error(errorData.error || 'Failed to claim file');
        } catch {
          throw new Error('Failed to claim file');
        }
      } else {
        throw new Error(`Failed to claim file: ${response.status}`);
      }
    }

    const data = await response.json();
    console.log('File claimed successfully:', data);
    return data;
  } catch (error) {
    console.error('Error claiming file:', error);
    throw error;
  }
};

/**
 * Update file metadata (title, description, tags, collection)
 *
 * Updates file metadata fields. Only provided fields will be updated.
 * Requires authentication and file ownership.
 */
export const updateFileMetadata = async (
  fileId: string,
  metadata: {
    title?: string;
    description?: string;
    tags?: string[];
    list?: string;
    allowDownload?: boolean;
  },
  idToken: string
): Promise<AudioFile> => {
  try {
    if (!FILE_METADATA_URL) {
      throw new Error('FILE_METADATA_URL not configured');
    }

    const url = `${FILE_METADATA_URL}/${fileId}`;
    console.log('Updating file metadata:', url, metadata);

    const response = await fetch(url, {
      method: 'PATCH',
      headers: {
        'Authorization': `Bearer ${idToken}`,
        'Content-Type': 'application/json',
      },
      body: JSON.stringify(metadata),
    });

    if (!response.ok) {
      const errorText = await response.text();
      console.error('Update metadata failed:', response.status, errorText);

      if (response.status === 401) {
        throw new Error('Authentication required to update file metadata');
      } else if (response.status === 403) {
        throw new Error('You do not have permission to modify this file');
      } else if (response.status === 404) {
        throw new Error('File not found');
      } else if (response.status === 400) {
        try {
          const errorData = JSON.parse(errorText);
          throw new Error(errorData.error || 'Invalid metadata');
        } catch {
          throw new Error('Invalid metadata');
        }
      } else {
        throw new Error(`Failed to update metadata: ${response.status}`);
      }
    }

    const data = await response.json();
    console.log('File metadata updated successfully:', data);
    return data;
  } catch (error) {
    console.error('Error updating file metadata:', error);
    throw error;
  }
};

/**
 * Get user's distinct tags and collections
 * Returns all unique tags and collections from user's files for autocomplete/suggestions
 * Requires authentication
 */
export const getUserMetadata = async (idToken: string): Promise<{ tags: string[]; lists: string[] }> => {
  try {
    if (!FILE_METADATA_URL) {
      throw new Error('FILE_METADATA_URL not configured');
    }

    // Remove trailing slash and file ID from base URL, then append /user-metadata
    const baseUrl = FILE_METADATA_URL.replace(/\/+$/, ''); // Remove trailing slashes
    const url = `${baseUrl}/user-metadata`;
    console.log('Fetching user metadata from:', url);

    const response = await fetch(url, {
      method: 'GET',
      headers: {
        'Authorization': `Bearer ${idToken}`,
        'Content-Type': 'application/json',
      },
    });

    if (!response.ok) {
      const errorText = await response.text();
      console.error('Get user metadata failed:', response.status, errorText);

      if (response.status === 401) {
        throw new Error('Authentication required to fetch user metadata');
      } else {
        throw new Error(`Failed to fetch user metadata: ${response.status}`);
      }
    }

    const data = await response.json();
    console.log('User metadata retrieved:', data);
    return data;
  } catch (error) {
    console.error('Error fetching user metadata:', error);
    // Return empty arrays on error to avoid breaking the UI
    return { tags: [], lists: [] };
  }
};

/**
 * Apply a promo code to upgrade user's plan
 * Requires authentication - must pass ID token
 */
export const applyPromoCode = async (code: string, idToken: string): Promise<PromoCodeResponse> => {
  try {
    if (!PROMO_CODE_URL) {
      throw new Error('PROMO_CODE_URL not configured');
    }

    console.log('Applying promo code:', code);

    const response = await fetch(PROMO_CODE_URL, {
      method: 'POST',
      headers: {
        'Content-Type': 'application/json',
        'Authorization': `Bearer ${idToken}`,
      },
      body: JSON.stringify({ code }),
    });

    const data = await response.json();

    if (!response.ok) {
      if (response.status === 401) {
        throw new Error('Authentication required to apply promo codes');
      }
      throw new Error(data.message || 'Failed to apply promo code');
    }

    console.log('Promo code applied successfully:', data);
    return data;
  } catch (error) {
    console.error('Error applying promo code:', error);
    throw error;
  }
};

// ─── Public profiles ( /@handle ) ──────────────────────────────────────────

export interface OwnProfile {
  handle: string;
  public_name: string;
  location: string;
  bio: string;
  enabled: boolean; // false = public page taken down (handle still reserved)
}

export interface PublicProfile {
  public_name: string;
  location?: string;
  bio?: string;
  collections: Collection[];
}

export class HandleTakenError extends Error {
  constructor() { super('handle_taken'); this.name = 'HandleTakenError'; }
}
export class ProRequiredError extends Error {
  constructor() { super('pro_required'); this.name = 'ProRequiredError'; }
}

/**
 * Server's view of the video render state when it refuses a remake/delete
 * request because the file isn't in a state that supports the operation. The
 * server returns 409 with `{state: 'idle'|'pending'|'failed'|'ready'}` so the
 * client can re-route (e.g. "remake on idle" silently becomes "kick off the
 * first render") without bouncing the user through an error toast.
 *
 * `state` is the server's reading at the moment of the 409 — the client
 * should treat it as authoritative and reconcile its optimistic UI to it.
 */
export type VideoRenderState = 'idle' | 'pending' | 'failed' | 'ready';
export class VideoRenderConflictError extends Error {
  state: VideoRenderState;
  constructor(state: VideoRenderState) {
    super(`video_render_conflict:${state}`);
    this.name = 'VideoRenderConflictError';
    this.state = state;
  }
}

/**
 * State the server reports when a kept-original GET or DELETE can't be served
 * in the file's current condition. `absent` = no kept-original ever existed
 * (or it was already deleted); `pending` = audio processor is still flipping
 * the sentinel and a delete would race; `failed` = retention failed at upload
 * time. The handlers treat each branch differently — `absent` reconciles to
 * "the slot is empty," `pending` shows a brief toast, `failed` surfaces the
 * reason if we have one.
 */
export type KeptOriginalState = 'absent' | 'pending' | 'failed';
export class KeptOriginalConflictError extends Error {
  state: KeptOriginalState;
  constructor(state: KeptOriginalState) {
    super(`kept_original_conflict:${state}`);
    this.name = 'KeptOriginalConflictError';
    this.state = state;
  }
}

const profileBase = (): string => {
  if (!USER_STORAGE_URL) throw new Error('USER_STORAGE_URL not configured');
  return USER_STORAGE_URL.replace(/\/$/, '');
};

/** Authenticated self-read of the caller's own profile. Empty strings when none set. */
export const getOwnProfile = async (idToken: string): Promise<OwnProfile> => {
  const response = await fetch(`${profileBase()}/profile`, {
    headers: { 'Authorization': `Bearer ${idToken}` },
  });
  if (!response.ok) {
    throw new Error(`Failed to load profile: ${response.status}`);
  }
  const d = await response.json();
  return {
    handle: d.handle || '',
    public_name: d.public_name || '',
    location: d.location || '',
    bio: d.bio || '',
    enabled: d.enabled !== false,
  };
};

/** Authenticated, Pro-gated. Claim/update handle + public display fields. */
export const saveProfile = async (
  data: { handle: string; public_name: string; location: string; bio: string; enabled: boolean },
  idToken: string
): Promise<OwnProfile> => {
  const response = await fetch(`${profileBase()}/profile`, {
    method: 'PUT',
    headers: { 'Content-Type': 'application/json', 'Authorization': `Bearer ${idToken}` },
    body: JSON.stringify(data),
  });
  if (response.status === 409) throw new HandleTakenError();
  if (response.status === 403) throw new ProRequiredError();
  if (!response.ok) {
    const err = await response.json().catch(() => ({} as Record<string, string>));
    throw new Error(err.error || err.message || `Failed to save profile: ${response.status}`);
  }
  const d = await response.json();
  return {
    handle: d.handle || data.handle,
    public_name: d.public_name || data.public_name,
    location: d.location || '',
    bio: d.bio || '',
    enabled: d.enabled !== false,
  };
};

/** Public, unauthenticated. Resolve a handle to its public profile + public collections. */
export const getPublicProfile = async (handle: string): Promise<PublicProfile> => {
  const clean = handle.replace(/^@/, '');
  const response = await fetch(`${profileBase()}/@${encodeURIComponent(clean)}`);
  if (response.status === 404) throw new Error('not_found');
  if (!response.ok) throw new Error(`Failed to load profile: ${response.status}`);
  const d = await response.json();
  return {
    public_name: d.public_name || '',
    location: d.location || undefined,
    bio: d.bio || undefined,
    collections: Array.isArray(d.collections) ? d.collections : [],
  };
};

/**
 * Create a new collection
 * Requires authentication - must pass ID token
 */
export const createCollection = async (
  name: string,
  description: string | undefined,
  visibility: 'public' | 'private',
  idToken: string
): Promise<Collection> => {
  try {
    if (!COLLECTIONS_URL) {
      throw new Error('COLLECTIONS_URL not configured');
    }

    console.log('Creating collection:', { name, description, visibility });

    const requestBody = {
      name,
      description: description || '',
      visibility,
    };

    const response = await fetch(`${COLLECTIONS_URL}/collections`, {
      method: 'POST',
      headers: {
        'Content-Type': 'application/json',
        'Authorization': `Bearer ${idToken}`,
      },
      body: JSON.stringify(requestBody),
    });

    if (!response.ok) {
      const errorText = await response.text();
      console.error('Create collection failed:', response.status, errorText);

      if (response.status === 401) {
        throw new Error('Authentication required to create collections');
      } else if (response.status === 400) {
        try {
          const errorData = JSON.parse(errorText);
          throw new Error(errorData.error || 'Invalid collection data');
        } catch {
          throw new Error('Invalid collection data');
        }
      } else {
        throw new Error(`Failed to create collection: ${response.status}`);
      }
    }

    const data = await response.json();
    console.log('Collection created successfully:', data);
    return data;
  } catch (error) {
    console.error('Error creating collection:', error);
    throw error;
  }
};

/**
 * Get all collections for the authenticated user
 * Requires authentication - must pass ID token
 */
export const getCollections = async (idToken: string): Promise<Collection[]> => {
  try {
    if (!COLLECTIONS_URL) {
      console.warn('COLLECTIONS_URL not configured');
      return [];
    }

    console.log('Fetching user collections');

    const response = await fetch(`${COLLECTIONS_URL}/collections`, {
      method: 'GET',
      headers: {
        'Content-Type': 'application/json',
        'Authorization': `Bearer ${idToken}`,
      },
    });

    if (!response.ok) {
      if (response.status === 404) {
        console.log('No collections found');
        return [];
      }
      throw new Error(`Failed to fetch collections: ${response.statusText}`);
    }

    const data = await response.json();
    console.log('Collections retrieved:', data);

    // Lambda returns array directly (not wrapped in {collections: []})
    return Array.isArray(data) ? data : [];
  } catch (error) {
    console.error('Error fetching collections:', error);
    return [];
  }
};

/**
 * Get a single collection by ID
 * Authentication optional - public collections can be viewed without auth
 */
export const getCollection = async (collectionId: string, idToken?: string): Promise<Collection> => {
  try {
    if (!COLLECTIONS_URL) {
      throw new Error('COLLECTIONS_URL not configured');
    }

    console.log('Fetching collection:', collectionId);

    const headers: Record<string, string> = {
      'Content-Type': 'application/json',
    };

    if (idToken) {
      headers['Authorization'] = `Bearer ${idToken}`;
    }

    const response = await fetch(`${COLLECTIONS_URL}/collection/${collectionId}`, {
      method: 'GET',
      headers,
    });

    if (!response.ok) {
      const errorText = await response.text();
      console.error('Get collection failed:', response.status, errorText);

      if (response.status === 404) {
        throw new Error('Collection not found');
      } else if (response.status === 403) {
        throw new Error('This collection is private');
      } else {
        throw new Error(`Failed to fetch collection: ${response.status}`);
      }
    }

    const data = await response.json();
    console.log('Collection retrieved:', data);
    // Lambda returns the collection object directly
    return data;
  } catch (error) {
    console.error('Error fetching collection:', error);
    throw error;
  }
};

/**
 * Update collection metadata (name, description, visibility)
 * Requires authentication and collection ownership
 */
export const updateCollection = async (
  collectionId: string,
  updates: {
    name?: string;
    description?: string;
    visibility?: 'public' | 'private';
    remove_artwork?: boolean;
  },
  idToken: string
): Promise<Collection> => {
  try {
    if (!COLLECTIONS_URL) {
      throw new Error('COLLECTIONS_URL not configured');
    }

    console.log('Updating collection:', collectionId, updates);

    const response = await fetch(`${COLLECTIONS_URL}/collection/${collectionId}`, {
      method: 'PUT',
      headers: {
        'Content-Type': 'application/json',
        'Authorization': `Bearer ${idToken}`,
      },
      body: JSON.stringify(updates),
    });

    if (!response.ok) {
      const errorText = await response.text();
      console.error('Update collection failed:', response.status, errorText);

      if (response.status === 401) {
        throw new Error('Authentication required to update collections');
      } else if (response.status === 403) {
        throw new Error('You do not have permission to modify this collection');
      } else if (response.status === 404) {
        throw new Error('Collection not found');
      } else if (response.status === 400) {
        try {
          const errorData = JSON.parse(errorText);
          throw new Error(errorData.error || 'Invalid collection data');
        } catch {
          throw new Error('Invalid collection data');
        }
      } else {
        throw new Error(`Failed to update collection: ${response.status}`);
      }
    }

    const data = await response.json();
    console.log('Collection updated successfully:', data);
    // Lambda returns the collection object directly
    return data;
  } catch (error) {
    console.error('Error updating collection:', error);
    throw error;
  }
};

/**
 * Delete a collection
 * Requires authentication and collection ownership
 */
export const deleteCollection = async (collectionId: string, idToken: string): Promise<void> => {
  try {
    if (!COLLECTIONS_URL) {
      throw new Error('COLLECTIONS_URL not configured');
    }

    console.log('Deleting collection:', collectionId);

    const response = await fetch(`${COLLECTIONS_URL}/collection/${collectionId}`, {
      method: 'DELETE',
      headers: {
        'Authorization': `Bearer ${idToken}`,
        'Content-Type': 'application/json',
      },
    });

    if (!response.ok) {
      const errorText = await response.text();
      console.error('Delete collection failed:', response.status, errorText);

      if (response.status === 401) {
        throw new Error('Authentication required to delete collections');
      } else if (response.status === 403) {
        throw new Error('You do not have permission to delete this collection');
      } else if (response.status === 404) {
        throw new Error('Collection not found');
      } else {
        throw new Error(`Failed to delete collection: ${response.status}`);
      }
    }

    console.log('Collection deleted successfully');
  } catch (error) {
    console.error('Error deleting collection:', error);
    throw error;
  }
};

/**
 * Add a file to a collection
 * Requires authentication and collection ownership
 */
export const addFileToCollection = async (
  fileId: string,
  collectionId: string,
  idToken: string
): Promise<{ success: boolean; message: string }> => {
  try {
    if (!COLLECTIONS_URL) {
      throw new Error('COLLECTIONS_URL not configured');
    }

    console.log('Adding file to collection:', { fileId, collectionId });

    const response = await fetch(`${COLLECTIONS_URL}/collection/${collectionId}/files`, {
      method: 'POST',
      headers: {
        'Content-Type': 'application/json',
        'Authorization': `Bearer ${idToken}`,
      },
      body: JSON.stringify({ file_id: fileId }),
    });

    if (!response.ok) {
      const errorText = await response.text();
      console.error('Add file to collection failed:', response.status, errorText);

      if (response.status === 401) {
        throw new Error('Authentication required');
      } else if (response.status === 403) {
        throw new Error('You do not have permission to modify this collection');
      } else if (response.status === 404) {
        throw new Error('Collection or file not found');
      } else if (response.status === 400) {
        try {
          const errorData = JSON.parse(errorText);
          throw new Error(errorData.error || 'Invalid request');
        } catch {
          throw new Error('Invalid request');
        }
      } else {
        throw new Error(`Failed to add file to collection: ${response.status}`);
      }
    }

    const data = await response.json();
    console.log('File added to collection successfully:', data);
    return { success: true, message: data.message || 'File added to collection' };
  } catch (error) {
    console.error('Error adding file to collection:', error);
    throw error;
  }
};

/**
 * Remove a file from a collection
 * Requires authentication and collection ownership
 */
export const removeFileFromCollection = async (
  fileId: string,
  collectionId: string,
  idToken: string
): Promise<{ success: boolean; message: string }> => {
  try {
    if (!COLLECTIONS_URL) {
      throw new Error('COLLECTIONS_URL not configured');
    }

    console.log('Removing file from collection:', { fileId, collectionId });

    const response = await fetch(`${COLLECTIONS_URL}/collection/${collectionId}/files/${fileId}`, {
      method: 'DELETE',
      headers: {
        'Content-Type': 'application/json',
        'Authorization': `Bearer ${idToken}`,
      },
    });

    if (!response.ok) {
      const errorText = await response.text();
      console.error('Remove file from collection failed:', response.status, errorText);

      if (response.status === 401) {
        throw new Error('Authentication required');
      } else if (response.status === 403) {
        throw new Error('You do not have permission to modify this collection');
      } else if (response.status === 404) {
        throw new Error('Collection or file not found');
      } else {
        throw new Error(`Failed to remove file from collection: ${response.status}`);
      }
    }

    const data = await response.json();
    console.log('File removed from collection successfully:', data);
    return { success: true, message: data.message || 'File removed from collection' };
  } catch (error) {
    console.error('Error removing file from collection:', error);
    throw error;
  }
};

/**
 * Reorder files in a collection
 * Requires authentication and ownership
 */
export const reorderCollectionFiles = async (
  collectionId: string,
  fileIds: string[],
  idToken: string
): Promise<{ success: boolean; message: string; files_count: number }> => {
  try {
    if (!COLLECTIONS_URL) {
      throw new Error('COLLECTIONS_URL not configured');
    }

    console.log('Reordering collection files:', { collectionId, fileIds });

    const response = await fetch(`${COLLECTIONS_URL}/collection/${collectionId}/files/order`, {
      method: 'PUT',
      headers: {
        'Content-Type': 'application/json',
        'Authorization': `Bearer ${idToken}`,
      },
      body: JSON.stringify({ file_ids: fileIds }),
    });

    if (!response.ok) {
      const errorText = await response.text();
      console.error('Reorder collection files failed:', response.status, errorText);

      if (response.status === 401) {
        throw new Error('Authentication required');
      } else if (response.status === 403) {
        throw new Error('You do not have permission to modify this collection');
      } else if (response.status === 404) {
        throw new Error('Collection not found');
      } else {
        throw new Error(`Failed to reorder files: ${response.status}`);
      }
    }

    const data = await response.json();
    console.log('Collection files reordered successfully:', data);
    return {
      success: true,
      message: data.message || 'Files reordered successfully',
      files_count: data.files_count || fileIds.length
    };
  } catch (error) {
    console.error('Error reordering collection files:', error);
    throw error;
  }
};

/**
 * Get all collections containing a specific file
 * Authentication optional - returns public + user's private collections
 */
export const getFileCollections = async (
  fileId: string,
  idToken?: string
): Promise<Collection[]> => {
  try {
    if (!COLLECTIONS_URL) {
      console.warn('COLLECTIONS_URL not configured');
      return [];
    }

    console.log('Fetching collections for file:', fileId);

    const headers: Record<string, string> = {
      'Content-Type': 'application/json',
    };

    if (idToken) {
      headers['Authorization'] = `Bearer ${idToken}`;
    }

    const response = await fetch(`${COLLECTIONS_URL}/file/${fileId}/collections`, {
      method: 'GET',
      headers,
    });

    if (!response.ok) {
      if (response.status === 404) {
        console.log('No collections found for file');
        return [];
      }
      throw new Error(`Failed to fetch file collections: ${response.statusText}`);
    }

    const data = await response.json();
    console.log('File collections retrieved:', data);
    return Array.isArray(data) ? data : [];
  } catch (error) {
    console.error('Error fetching file collections:', error);
    return [];
  }
};

/**
 * Get files in a collection
 * Authentication optional - public collections can be viewed without auth
 */
export const getCollectionFiles = async (collectionId: string, idToken?: string): Promise<CollectionFile[]> => {
  try {
    if (!COLLECTIONS_URL) {
      console.warn('COLLECTIONS_URL not configured');
      return [];
    }

    console.log('Fetching collection files:', collectionId);

    const headers: Record<string, string> = {
      'Content-Type': 'application/json',
    };

    if (idToken) {
      headers['Authorization'] = `Bearer ${idToken}`;
    }

    const response = await fetch(`${COLLECTIONS_URL}/collection/${collectionId}/files`, {
      method: 'GET',
      headers,
    });

    if (!response.ok) {
      if (response.status === 404) {
        console.log('No files found in collection');
        return [];
      } else if (response.status === 403) {
        throw new Error('This collection is private');
      }
      throw new Error(`Failed to fetch collection files: ${response.statusText}`);
    }

    const data = await response.json();
    console.log('Collection files retrieved:', data);
    // Lambda returns the array directly, not wrapped in { files: [...] }
    return Array.isArray(data) ? data : [];
  } catch (error) {
    console.error('Error fetching collection files:', error);
    throw error;
  }
};

/**
 * Upload artwork for a collection (Two-step presigned URL pattern)
 * Requires authentication and collection ownership
 */
export const uploadCollectionArtwork = async (
  collectionId: string,
  artworkFile: File,
  idToken: string,
  onProgress?: (pct: number) => void
): Promise<{ success: boolean; message: string }> => {
  try {
    if (!COLLECTIONS_URL) {
      throw new Error('COLLECTIONS_URL not configured');
    }

    // Validate image file
    if (!artworkFile.type.startsWith('image/')) {
      throw new Error('Only image files are allowed for artwork');
    }

    // Max artwork size: 10MB
    const maxSize = 10 * 1024 * 1024;
    if (artworkFile.size > maxSize) {
      throw new Error('Artwork file too large. Maximum size is 10MB');
    }

    console.log('Requesting presigned URL for collection artwork:', {
      collectionId,
      fileName: artworkFile.name,
      fileSize: artworkFile.size,
      contentType: artworkFile.type
    });

    // Step 1: Request presigned URL from backend
    const presignResponse = await fetch(`${COLLECTIONS_URL}/collection/${collectionId}/artwork`, {
      method: 'PUT',
      headers: {
        'Content-Type': 'application/json',
        'Authorization': `Bearer ${idToken}`,
      },
      body: JSON.stringify({
        content_type: artworkFile.type,
        file_size: artworkFile.size
      }),
    });

    if (!presignResponse.ok) {
      const errorText = await presignResponse.text();
      console.error('Failed to get presigned URL:', presignResponse.status, errorText);

      if (presignResponse.status === 401) {
        throw new Error('Authentication required to upload artwork');
      } else if (presignResponse.status === 403) {
        throw new Error('You do not have permission to modify this collection');
      } else if (presignResponse.status === 404) {
        throw new Error('Collection not found');
      } else {
        throw new Error('Failed to generate upload URL');
      }
    }

    const presignData = await presignResponse.json();
    const { signed_url } = presignData;

    if (!signed_url) {
      throw new Error('No presigned URL returned from backend');
    }

    console.log('Uploading artwork to S3...');

    // Step 2: Upload file directly to S3 via presigned URL. Use XHR (not fetch)
    // so we can pipe upload-progress events back through the optional onProgress
    // callback — fetch() doesn't expose upload progress, which leaves the Save
    // button stuck on a generic "Uploading…" spinner with no feedback.
    await new Promise<void>((resolve, reject) => {
      const xhr = new XMLHttpRequest();
      xhr.open('PUT', signed_url);
      xhr.setRequestHeader('Content-Type', artworkFile.type);
      if (onProgress) {
        xhr.upload.onprogress = (e) => {
          if (e.lengthComputable) {
            onProgress(Math.round((e.loaded / e.total) * 100));
          }
        };
      }
      xhr.onload = () => {
        if (xhr.status >= 200 && xhr.status < 300) {
          resolve();
        } else {
          console.error('S3 upload failed:', xhr.status, xhr.responseText);
          reject(new Error('Failed to upload artwork to storage'));
        }
      };
      xhr.onerror = () => reject(new Error('Network error during artwork upload'));
      xhr.onabort = () => reject(new Error('Artwork upload aborted'));
      xhr.send(artworkFile);
    });

    console.log('Artwork uploaded successfully to S3');
    return {
      success: true,
      message: 'Artwork uploaded successfully. Processing...'
    };
  } catch (error) {
    console.error('Error uploading collection artwork:', error);
    throw error;
  }
};

// ==================== Collection Links API ====================

export interface CollectionLink {
  short_id: string;
  collection_id: string;
  owner: string;
  created_at: number;
  expires_at?: number;
  password_hint?: string;
  click_count: number;
  last_accessed?: number;
  status: 'active' | 'revoked';
  label?: string;
}

export interface CreateLinkRequest {
  collection_id: string;
  expires_in?: number; // seconds from now
  password?: string;
  password_hint?: string;
  label?: string;
}

export interface UpdateLinkRequest {
  password?: string;
  password_hint?: string;
  expires_in?: number;
  status?: 'active' | 'revoked';
  label?: string;
}

export interface ResolveLinkResponse {
  collection_id?: string;
  requires_password: boolean;
  password_hint?: string;
  label?: string;
}

// Create a new short link for a collection
export const createCollectionLink = async (request: CreateLinkRequest): Promise<CollectionLink> => {
  try {
    const idToken = localStorage.getItem('idToken');
    if (!idToken) {
      throw new Error('Authentication required');
    }

    const response = await fetch(`${COLLECTION_LINKS_URL}/links`, {
      method: 'POST',
      headers: {
        'Content-Type': 'application/json',
        'Authorization': `Bearer ${idToken}`,
      },
      body: JSON.stringify(request),
    });

    if (!response.ok) {
      const errorText = await response.text();
      console.error('Create link failed:', response.status, errorText);

      if (response.status === 401) {
        throw new Error('Authentication required');
      } else if (response.status === 403) {
        throw new Error('You do not own this collection');
      } else {
        throw new Error('Failed to create link');
      }
    }

    return await response.json();
  } catch (error) {
    console.error('Error creating collection link:', error);
    throw error;
  }
};

// Resolve a short link (without password)
export const resolveCollectionLink = async (shortId: string): Promise<ResolveLinkResponse> => {
  try {
    const response = await fetch(`${COLLECTION_LINKS_URL}/links/${shortId}/resolve`, {
      method: 'GET',
    });

    if (!response.ok) {
      throw response;
    }

    return await response.json();
  } catch (error) {
    console.error('Error resolving collection link:', error);
    throw error;
  }
};

// Resolve a short link with password
export const resolveCollectionLinkWithPassword = async (
  shortId: string,
  password: string
): Promise<ResolveLinkResponse> => {
  try {
    const response = await fetch(`${COLLECTION_LINKS_URL}/links/${shortId}/resolve`, {
      method: 'POST',
      headers: {
        'Content-Type': 'application/json',
      },
      body: JSON.stringify({ password }),
    });

    if (!response.ok) {
      throw response;
    }

    return await response.json();
  } catch (error) {
    console.error('Error resolving collection link with password:', error);
    throw error;
  }
};

// Update a link (password, expiration, status, label)
export const updateCollectionLink = async (
  shortId: string,
  request: UpdateLinkRequest
): Promise<{ success: boolean }> => {
  try {
    const idToken = localStorage.getItem('idToken');
    if (!idToken) {
      throw new Error('Authentication required');
    }

    const response = await fetch(`${COLLECTION_LINKS_URL}/links/${shortId}`, {
      method: 'PATCH',
      headers: {
        'Content-Type': 'application/json',
        'Authorization': `Bearer ${idToken}`,
      },
      body: JSON.stringify(request),
    });

    if (!response.ok) {
      const errorText = await response.text();
      console.error('Update link failed:', response.status, errorText);

      if (response.status === 401) {
        throw new Error('Authentication required');
      } else if (response.status === 403) {
        throw new Error('You do not own this link');
      } else if (response.status === 404) {
        throw new Error('Link not found');
      } else {
        throw new Error('Failed to update link');
      }
    }

    return await response.json();
  } catch (error) {
    console.error('Error updating collection link:', error);
    throw error;
  }
};

// Revoke a link
export const revokeCollectionLink = async (shortId: string): Promise<{ success: boolean }> => {
  try {
    const idToken = localStorage.getItem('idToken');
    if (!idToken) {
      throw new Error('Authentication required');
    }

    const response = await fetch(`${COLLECTION_LINKS_URL}/links/${shortId}`, {
      method: 'DELETE',
      headers: {
        'Authorization': `Bearer ${idToken}`,
      },
    });

    if (!response.ok) {
      const errorText = await response.text();
      console.error('Revoke link failed:', response.status, errorText);

      if (response.status === 401) {
        throw new Error('Authentication required');
      } else if (response.status === 403) {
        throw new Error('You do not own this link');
      } else if (response.status === 404) {
        throw new Error('Link not found');
      } else {
        throw new Error('Failed to revoke link');
      }
    }

    return await response.json();
  } catch (error) {
    console.error('Error revoking collection link:', error);
    throw error;
  }
};

// Submit an anonymous-uploader email for a pre-expiry reminder.
//
// URL-gated: the caller (SignInToClaimBanner) uses the *presence* of
// REACT_APP_EMAIL_REMINDER_URL to decide whether to render the capture
// affordance at all. One env var, one truth. If the var is unset, the UI
// never invokes this function; if it's set to a real Lambda Function URL,
// the POST goes there. This means Gilfoyle's backend build is dark-launched
// safely — until the env var is added to the webapp build config, users
// never see Zone 2.
//
// Contract: POST base_url with { email, fileIds } → 200 (captured)
// | 400 (invalid email / rate-limited) | 5xx (retry). No auth header —
// this endpoint is anonymous-friendly by design (the whole capture premise
// is "reader who won't sign in").
export const submitEmailReminder = async (email: string, fileIds: string[]): Promise<void> => {
  const url = normalizeBaseUrl(process.env.REACT_APP_EMAIL_REMINDER_URL || '');
  if (!url) {
    // Defensive fallback — the caller should never invoke us when the URL
    // is unset (Zone 2 wouldn't render), but surface loudly if it happens.
    throw new Error('email reminder URL not configured');
  }
  const response = await fetch(url, {
    method: 'POST',
    headers: { 'Content-Type': 'application/json' },
    body: JSON.stringify({ email, fileIds }),
  });
  if (!response.ok) {
    if (response.status === 400) {
      throw new Error('Please check that email — the server didn’t accept it.');
    }
    if (response.status === 429) {
      throw new Error('That email was already submitted recently. It’s on the list.');
    }
    throw new Error('Something went wrong. Try again in a moment.');
  }
};

// List all links for a collection
export const listCollectionLinks = async (collectionId: string): Promise<CollectionLink[]> => {
  try {
    const idToken = localStorage.getItem('idToken');
    if (!idToken) {
      throw new Error('Authentication required');
    }

    const response = await fetch(`${COLLECTION_LINKS_URL}/collections/${collectionId}/links`, {
      method: 'GET',
      headers: {
        'Authorization': `Bearer ${idToken}`,
      },
    });

    if (!response.ok) {
      const errorText = await response.text();
      console.error('List links failed:', response.status, errorText);

      if (response.status === 401) {
        throw new Error('Authentication required');
      } else if (response.status === 403) {
        throw new Error('You do not own this collection');
      } else {
        throw new Error('Failed to list links');
      }
    }

    const data = await response.json();
    return data.links || [];
  } catch (error) {
    console.error('Error listing collection links:', error);
    throw error;
  }
};
